import { FC, useEffect, useRef, type CSSProperties } from 'react';
import { V4 as C } from '@components/v4/tokens';
import { Z } from '@components/v4';
import type { InviteSurface } from '@utils/featureRollout/types';
import { AGENT_CHAT_DISCOVERY } from '@utils/featureRollout/strings';
import { usePaulinaDialogA11y } from './usePaulinaDialogA11y';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

/**
 * PaulinaInvite — invitación / anuncio "Conoce a Paulina, pruébala" para el rollout de la
 * feature agent-chat. Se muestra cuando `useAgentChatGate().showInvite` es true (variante
 * control + experimento activo + el backend autoriza invitar ahora). Patrón visual heredado
 * de FeatureCoachmark / PaulinaNudgeBubble (caret + dismiss + role=dialog; base `opacity:1`
 * animando SOLO `translateY` — un keyframe con opacidad dentro de un contenedor con transform
 * puede quedar stuck en 0).
 *
 * Respeta `invite_surface`:
 *   - 'card'  → coachmark anclado cerca de la entrada de Paulina (pill en desktop, avatar del
 *               header en móvil). z-index `Z.paulinaPill` (80) para NO tapar el toast de pedido.
 *   - 'modal' → diálogo centrado (overlay), a11y de modal vía usePaulinaDialogA11y.
 *
 * Telemetría (pipeline canónico useTracking, igual que el resto de Paulina): al mostrarse
 * `gate.shown()` + PaulinaInviteShown; aceptar → PaulinaInviteAccepted; cerrar/después → PaulinaInviteDismissed.
 * La cesión ante pedidos (no encima el toast operativo) la decide el host (no se monta si hay
 * pedido), igual que el nudge.
 */

const KF_ID = 'plick-paulina-invite-kf';
if (typeof document !== 'undefined' && !document.getElementById(KF_ID)) {
  const st = document.createElement('style');
  st.id = KF_ID;
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '@keyframes plick-paulina-invite-in{from{transform:translateY(8px)}to{transform:translateY(0)}}' +
    '.plick-paulina-invite-anim{animation:plick-paulina-invite-in 220ms ease-out both}}';
  document.head.appendChild(st);
}

interface PaulinaInviteProps {
  /** 'card' = coachmark anclado; 'modal' = diálogo centrado. */
  surface: InviteSurface;
  /** Acepta: activa/abre Paulina (gate.accept + abrir chat). */
  onAccept: () => void;
  /** Cierra/descarta (gate.dismiss = snooze). */
  onDismiss: () => void;
  /** Registra la exhibición (gate.shown). Se llama una vez al montar. */
  onShown: () => void;
  /** Ancla del coachmark: 'desktop' = sobre la pill (abajo-derecha); 'mobile' = bajo el avatar del header. */
  anchor?: 'desktop' | 'mobile';
}

const Badge: FC = () => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '.08em',
      background: C.accent,
      color: C.white,
      borderRadius: 9999,
      padding: '2px 8px',
      alignSelf: 'flex-start',
    }}
  >
    {AGENT_CHAT_DISCOVERY.badge}
  </span>
);

const DismissIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const PaulinaInvite: FC<PaulinaInviteProps> = ({
  surface,
  onAccept,
  onDismiss,
  onShown,
  anchor = 'desktop',
}) => {
  const { trackEvent } = useTracking();
  // Registrar la exhibición una sola vez (gate.shown + PaulinaInviteShown). El dedupe durable
  // (una vez por sesión por comercio+feature) lo gobierna el framework dentro de markInvitationShown.
  const shownOnce = useRef(false);
  useEffect(() => {
    if (shownOnce.current) return;
    shownOnce.current = true;
    onShown();
    trackEvent(TRACK_EVENT_NAMES.PaulinaInviteShown, { surface });
  }, [onShown, surface]);

  const accept = () => {
    trackEvent(TRACK_EVENT_NAMES.PaulinaInviteAccepted, { surface });
    onAccept();
  };
  const dismiss = () => {
    trackEvent(TRACK_EVENT_NAMES.PaulinaInviteDismissed, { surface });
    onDismiss();
  };

  // A11y de modal solo en la variante 'modal'; en 'card' el ref queda sin usar (coachmark no
  // atrapa foco). El hook se llama SIEMPRE (regla de hooks) pero queda inerte en 'card':
  // así el coachmark NO se cierra con Escape global ni restaura el foco al desmontar (eso es
  // comportamiento de modal). `enabled` controla los efectos sin romper la regla de hooks.
  const dialogRef = usePaulinaDialogA11y(dismiss, surface === 'modal');

  const titleId = 'paulina-invite-title';
  const bodyId = 'paulina-invite-body';

  const content = (
    <>
      <div className="mb-1 flex items-start justify-between gap-2">
        <Badge />
        <button
          type="button"
          aria-label={AGENT_CHAT_DISCOVERY.dismiss}
          onClick={dismiss}
          style={{
            background: 'transparent',
            border: 0,
            color: 'rgba(255,255,255,.7)',
            cursor: 'pointer',
            padding: 0,
            marginTop: -2,
          }}
        >
          <DismissIcon />
        </button>
      </div>
      <p id={titleId} className="mb-1 text-sm font-bold leading-snug">
        {AGENT_CHAT_DISCOVERY.title}
      </p>
      <p id={bodyId} className="mb-3 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,.82)' }}>
        {AGENT_CHAT_DISCOVERY.body}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={accept}
          className="rounded-lg px-3 py-2 text-sm font-semibold"
          style={{ background: C.white, color: C.primary }}
        >
          {AGENT_CHAT_DISCOVERY.cta}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="text-xs font-semibold underline"
          style={{ background: 'transparent', border: 0, color: 'rgba(255,255,255,.75)', cursor: 'pointer' }}
        >
          {AGENT_CHAT_DISCOVERY.dismiss}
        </button>
      </div>
    </>
  );

  // ── MODAL: diálogo centrado sobre overlay ─────────────────────────────────────────────
  if (surface === 'modal') {
    const overlay: CSSProperties = {
      position: 'fixed',
      inset: 0,
      zIndex: Z.paulinaSheet, // bajo el toast de pedido (Z.pedido=99); el host no lo monta si hay pedido.
      background: 'rgba(37,55,70,.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    };
    const card: CSSProperties = {
      width: '100%',
      maxWidth: 360,
      background: C.primary,
      color: C.white,
      borderRadius: 14,
      padding: '18px 18px 16px',
      boxShadow: '0 18px 48px rgba(37,55,70,.4)',
    };
    return (
      <div style={overlay} onClick={(e) => e.target === e.currentTarget && dismiss()}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          style={card}
        >
          {content}
        </div>
      </div>
    );
  }

  // ── CARD: coachmark anclado cerca de la entrada de Paulina ────────────────────────────
  // Desktop: sobre la pill (abajo-derecha, caret apuntando abajo). Móvil: bajo el avatar del
  // header (arriba-derecha, caret apuntando arriba).
  const isMobile = anchor === 'mobile';
  const bubble: CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: 58, // justo debajo del header móvil (52px) + holgura.
        right: 10,
        zIndex: Z.paulinaPill,
        width: 280,
        background: C.primary,
        color: C.white,
        borderRadius: 12,
        padding: '13px 15px',
        boxShadow: '0 14px 36px rgba(37,55,70,.32)',
        opacity: 1,
      }
    : {
        position: 'fixed',
        right: 24,
        bottom: 78, // justo encima de la pill (bottom-6 + alto de la pill).
        zIndex: Z.paulinaPill,
        width: 280,
        background: C.primary,
        color: C.white,
        borderRadius: 12,
        padding: '13px 15px',
        boxShadow: '0 14px 36px rgba(37,55,70,.32)',
        opacity: 1,
      };

  const caret: CSSProperties = isMobile
    ? {
        position: 'absolute',
        top: -5,
        right: 22,
        width: 12,
        height: 12,
        background: C.primary,
        transform: 'rotate(45deg)',
        borderRadius: 2,
      }
    : {
        position: 'absolute',
        bottom: -5,
        right: 30,
        width: 12,
        height: 12,
        background: C.primary,
        transform: 'rotate(45deg)',
        borderRadius: 2,
      };

  return (
    <div
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className="plick-paulina-invite-anim"
      style={bubble}
    >
      <span aria-hidden="true" style={caret} />
      {content}
    </div>
  );
};
